import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { KitElement } from 'modules/teams/domain/enums/kit-element';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';

export interface SportKitElementsResult {
  /** Элементы формы, разрешённые для вида спорта, в порядке ответа сервера. */
  elements: KitElement[];
  isLoading: boolean;
  isError: boolean;
  /** Ответ (успешный или нет) уже получен — `hasCurrentData` для `deriveAsyncState`. */
  hasLoaded: boolean;
}

function isKitElement(value: string): value is KitElement {
  return (Object.values(KitElement) as string[]).includes(value);
}

/**
 * Загружает список элементов формы для вида спорта команды.
 *
 * Запрос живёт в хуке, а не в теле редактора: состояние формы больше не завязано
 * на `team.kits`, поэтому обновление команды (например, после `upsertTeamKits`,
 * который инвалидирует её) не перезапрашивает элементы и не стирает правки.
 *
 * RTK Query здесь намеренно не используется: `sportKitElements` пока живёт только
 * в репозитории, миграция запроса — отдельная задача.
 */
export function useSportKitElements(sportType?: string): SportKitElementsResult {
  const [elements, setElements] = useState<KitElement[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(sportType));
  const [isError, setIsError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!sportType) return;

    let cancelled = false;

    setIsLoading(true);
    setIsError(false);

    teamsRepository
      .getSportKitElements(sportType)
      .then((loaded) => {
        if (cancelled) return;
        setElements(loaded.filter(isKitElement));
        setHasLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setElements([]);
        setIsError(true);
        setHasLoaded(true);
        toast.error('Не удалось загрузить элементы формы');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sportType]);

  return { elements, isLoading, isError, hasLoaded };
}

export default useSportKitElements;
